import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Truck, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';
import MapComponent from './MapComponent';
import api from '../utils/api';
import { formatDateTime, formatTime } from '../utils/formatters';
import { getLoadDisplayId } from '../utils/loadUtils';

const ActiveLoadsMap = ({ expanded = false, onToggleExpand }) => {
  const [activeLoads, setActiveLoads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Format locations for the MapComponent
  const mapLocations = useMemo(() => {
    if (!activeLoads.length) return [];

    // Create an array of current locations only, without connecting them
    return activeLoads
      .filter(load => load.tracking?.latitude && load.tracking?.longitude)
      .map(load => ({
        id: `load-${load.id}`,
        stop_type: 'current',
        sequence: 0, // Not relevant for disconnected points
        location: {
          latitude: load.tracking.latitude,
          longitude: load.tracking.longitude,
          city: load.tracking.city || '',
          state: load.tracking.state || '',
          address: ''
        },
        business_name: `Load ${getLoadDisplayId(load)}`,
        timestamp: formatDateTime(load.tracking.timestamp),
        contact_name: load.assigned_driver?.name || 'Unassigned',
        contact_phone: '',
        contact_instructions: load.tracking.notes || '',
        load_id: load.id,
        load_uuid: load.uuid,
        load_display_id: getLoadDisplayId(load),
        status: load.status,
        origin_city: load.origin.city,
        origin_state: load.origin.state,
        destination_city: load.destination.city,
        destination_state: load.destination.state,
        is_current: true,
        // Include heading and speed for directional vehicle markers
        // Don't default speed to 0 - pass undefined if not available so we can distinguish
        // "no data" from "actually parked at 0 mph"
        heading: load.tracking.heading ?? load.tracking.bearing ?? null,
        speed: load.tracking.speed ?? null,
        source: load.tracking.source || 'app'
      }));
  }, [activeLoads]);

  const fetchActiveLoads = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/loads/active');
      setActiveLoads(response.data.active_loads);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching active loads:', err);
      setError('Failed to load active shipments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveLoads();
    
    // Set up polling for real-time updates
    const intervalId = setInterval(fetchActiveLoads, 60000); // Update every minute
    
    return () => clearInterval(intervalId);
  }, []);

  return (
    <motion.div 
      layout
      className={`bg-white shadow rounded-lg overflow-hidden ${expanded ? 'fixed inset-4 z-50' : 'h-[400px] mb-8'}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
        <div className="flex items-center">
          <MapPin className="h-5 w-5 text-blue-500 mr-2" />
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Active Shipments Map
          </h3>
          {activeLoads.length > 0 && (
            <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              {activeLoads.length} Active
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              Updated: {formatTime(lastUpdated)}
            </span>
          )}
          
          <button 
            onClick={fetchActiveLoads} 
            className="p-1 text-gray-500 hover:text-blue-500"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          
          <button 
            onClick={onToggleExpand} 
            className="p-1 text-gray-500 hover:text-blue-500"
          >
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
      
      <div className={`relative ${expanded ? 'h-[calc(100%-57px)]' : 'h-[340px]'}`}>
        {isLoading && activeLoads.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-75 z-10">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-2"></div>
              <p className="text-sm text-gray-600">Loading shipment data...</p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div className="text-center p-4">
              <p className="text-red-500 mb-2">{error}</p>
              <button 
                onClick={fetchActiveLoads}
                className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
              >
                Try Again
              </button>
            </div>
          </div>
        )}
        
        {activeLoads.length === 0 && !isLoading && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div className="text-center p-4">
              <Truck className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No active shipments found</p>
            </div>
          </div>
        )}
        
        <MapComponent 
          locations={mapLocations} 
          clustered={true} // Enable clustering for multiple loads
          showInfo={true} 
          isLoadStop={false} // Important: Set to false to prevent polylines
          isCarrier={false}
        />
      </div>
    </motion.div>
  );
};

export default ActiveLoadsMap; 